import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { Selector } from 'components/Form/Selectors/Selector';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import AssetHandler from 'handlers/asset.handler';
import { useEffect, useState } from 'react';
import { modalInputFieldStyle } from 'styles/global.css';
import { createOrUpdateAssetRequestBodySchema } from 'utils/schemas/asset.schema';
import { validateYupSchema } from 'utils/validators/schema.validator';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { type ICreateAssetPayload } from 'types/payloads/asset.payload.types';
import { ASSET_TYPES, FUEL_TYPES } from 'utils/enums';
import { type FUEL_TYPES_ENUM, type ASSET_TYPES_ENUM } from 'types/asset.types';
import { ThemePalette } from 'mui.theme';
import {
  handleSelectOperatorSingle,
  handleSelectOperatorAll,
  handleOperationalStatus,
  handleAssetTPMS,
} from 'views/Settings/ExternalUsers/AssetView/helpers/common';
import { useAppSelector } from 'store/hook';
import { TirePatternSelector } from './TirePatternSelector';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  title: string;
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  makeModelDetails: MakeModelDetails[];
  customerId: string;
}

export const CreateAsset = ({
  title,
  handleAbort,
  open,
  enableDarkTheme,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const assetHandler = new AssetHandler();
  const [isValidForm, setFormValidity] = useState(false);
  const [asset, setAsset] = useState<ICreateAssetPayload>({
    assetVin: '',
    assetType: 'light_truck',
    isActive: true,
    make: '',
    model: '',
    bumperNumber: '',
    engineHoursOffset: 0,
    odometerOffset: 0,
    fuelSource: 'Unknown',
    operators: [],
    externalId: null,
    assetNotes: [],
    tpmsEnabled: false,
  });

  const [assetTirePattern, setAssetTirePattern] = useState<string>('4x4');

  const { operators } = useAppSelector((state) => state.operatorReducer);
  const transformedOperators: Array<{ id: string; display: string }> =
    operators.map((operator) => {
      return {
        id: operator.operatorId,
        display: operator.operatorName,
      };
    });

  // Validate form as user fills out fields
  useEffect(() => {
    const validateForm = async () =>
      await validateYupSchema(asset, createOrUpdateAssetRequestBodySchema);

    void validateForm().then((isValid) => {
      setFormValidity(isValid);
    });
  }, [asset]);

  const handleIntegerFieldsValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let { value, name } = event.target;
    if (!value || isNaN(parseInt(value))) {
      value = '0';
    }
    setAsset({
      ...asset,
      [name]: parseInt(value),
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setAsset({
      ...asset,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    await assetHandler.createAsset({ ...asset, patternName: assetTirePattern });

    handleAbort();
  };
  /**
   * This is the form that will be rendered in the modal
   *
   * Asset VIN/ID
   */
  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: !isValidForm,
        onClick: handleSubmit,
        label: t('add_new_asset'),
      }}
      handleAbort={handleAbort}
      title={title}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      {/* Asset VIN/ID TextField */}
      <SWTextField
        key="assetVin"
        name="assetVin"
        label={t('asset_vin_id')}
        placeholder={t('enter_vin_id_number')}
        TextFieldProps={{
          name: 'assetVin',
          value: asset.assetVin,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Asset Type selector drop down single */}
      <Box key="assetType">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        >
          {t('asset_type_2')}
        </Typography>
        <Selector
          value={asset.assetType}
          onChange={(event) => {
            const selectedValue = event.target.value as ASSET_TYPES_ENUM;
            setAsset({
              ...asset,
              assetType: selectedValue,
            });
          }}
          selectorOptions={ASSET_TYPES}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      <SWTextField
        key="make"
        name="make"
        label={t('make')}
        placeholder={t('enter_asset_make')}
        TextFieldProps={{
          name: 'make',
          value: asset.make,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="model"
        name="model"
        label={t('model_2')}
        placeholder={t('enter_asset_model')}
        TextFieldProps={{
          name: 'model',
          value: asset.model,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="bumperNumber"
        name="bumperNumber"
        label={t('bumper_id_10')}
        placeholder={t('enter_bumper_id')}
        TextFieldProps={{
          name: 'bumperNumber',
          value: asset.bumperNumber,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* <FlexBox></FlexBox> */}

      {/* Fuel Source selector drop down single */}
      <Box key="fuelSource">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        >
          {t('fuel_source')}
        </Typography>
        <Selector
          value={asset.fuelSource}
          onChange={(event) => {
            const selectedValue = event.target.value as FUEL_TYPES_ENUM;
            setAsset({
              ...asset,
              fuelSource: selectedValue,
            });
          }}
          selectorOptions={FUEL_TYPES}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      <SWTextField
        key="odometerOffset"
        name="odometerOffset"
        label={t('odometer_reading_offset_km')}
        placeholder={t('enter_odometer_reading_offset')}
        TextFieldProps={{
          name: 'odometerOffset',
          value: asset.odometerOffset,
          onChange: handleIntegerFieldsValue,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="engineHoursOffset"
        name="engineHoursOffset"
        label={t('engine_hours_offset')}
        placeholder={t('enter_engine_hours_offset')}
        TextFieldProps={{
          name: 'engineHoursOffset',
          value: asset.engineHoursOffset,
          onChange: handleIntegerFieldsValue,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* External ID TextField */}
      <SWTextField
        key="externalId"
        name="externalId"
        label={t('external_id')}
        placeholder={t('enter_customer_identified_id')}
        TextFieldProps={{
          name: 'externalId',
          value: asset.externalId,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Operators multi selector */}
      <Box key="operators">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        >
          {t('operator_s')}
        </Typography>
        <MultiSelector
          isSelectionEmpty={true}
          messageIfSelectionEmpty="No operators available"
          value={asset.operators}
          renderValue={(selected) => {
            return `${selected?.length || 0} Operators(s) Selected`;
          }}
          handleSelectOne={(event) => {
            handleSelectOperatorSingle(event, asset, setAsset);
          }}
          handleSelectAll={() => {
            handleSelectOperatorAll(transformedOperators, asset, setAsset);
          }}
          /*
        This stubbed response be replaced with real customer accounts
        */
          selectorOptions={transformedOperators}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      {/* Operational Status Check Box */}
      <Box key="operationalStatus">
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: enableDarkTheme
                  ? palette.primary
                  : ThemePalette.typography.primary,
                '&.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          checked={!asset.isActive}
          label={t('asset_is_currently_out_of_service')}
          onChange={() => {
            handleOperationalStatus(asset, setAsset);
          }}
          sx={{
            pt: 2,
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: enableDarkTheme
                  ? palette.primary
                  : ThemePalette.typography.primary,
                '&.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          checked={asset.tpmsEnabled}
          label={t('enable_tpms')}
          onChange={() => {
            handleAssetTPMS(asset, setAsset);
          }}
          sx={{
            pt: 2,
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        />
      </Box>

      <SWTextField
        key="assetNotes"
        name="assetNotes"
        label={t('asset_notes')}
        placeholder={t('asset_notes_optional')}
        TextFieldProps={{
          name: 'assetNotes',
          // value: asset.assetNotes,
          onChange: handleChange,
          multiline: true,
          minRows: 1,
          fullWidth: true,
          InputProps: {
            sx: {
              WebkitBoxShadow: '0 0 0 1000px white inset',
            },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />
      <>
        {asset.tpmsEnabled && (
          <Box>
            <Typography
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
                marginBottom: '5px',
              }}
            >
              {t('tire_pattern')}
            </Typography>
            <TirePatternSelector
              enableDarkTheme={enableDarkTheme}
              value={assetTirePattern}
              setValue={setAssetTirePattern}
            />
          </Box>
        )}
      </>
    </InteractiveModal>
  );
};
